// Commissions-by-policy table — its own tab in the Reports group (below
// "Comisiones"). A grid across the whole policy book with filters by type /
// company / year, inline-editable %, monthly commission and total, plus
// effective/renewal dates and a total-commission footer (Lissette 2026-06-18;
// monthly + dates + computed total added 2026-06-22).
//
// COMISION POR MIEMBRO (Lissette 2026-09-11): en salud la aseguradora paga por
// PERSONA en la poliza, no un importe mensual. Por eso hay tres columnas mas
// -- personas aseguradas (automatica, de la poliza), cuanto por persona, y la
// razon del porcentaje (split / agencia, un desplegable que se llena solo) -- y
// el total de esas filas es (por persona x personas) x %. Convive con la cuenta
// de siempre en vez de sustituirla: manda solo cuando hay personas Y un importe
// por persona, asi que ninguna poliza ya cargada cambia de total sola. La
// aritmetica esta en crm-app/comisionPorPersona.js.
//
// The %/monthly/total live in the policy's type_details blob (commissionPercent
// / commissionMonthly / commissionAmount). IMPORTANT: saving sends the FULL
// policy object so mapPolicyToApi rebuilds type_details with every other
// type-specific field intact — a partial patch would wipe vehicle/payment/
// sale-date data. The effective/renewal dates are real policy columns
// (p.effectiveDate / p.renewalDate) and are read-only here.
import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { BarChart2, ExternalLink, ChevronLeft, ChevronRight, Columns as ColumnsIcon } from 'lucide-react';
import { Select, Combobox } from './ui.jsx';
import { toast } from './Toast.jsx';
import { fmt, fmtMoney, formatClientName, estaEscribiendo } from '../utils.js';
import { POLICY_TYPES, getPolicyType } from '../policyTypes.js';
// El estatus se pinta con la MISMA pieza que la tarjeta de la poliza, para que
// se vea igual y para que los estatus propios de la agencia salgan con su
// nombre y su color en vez de con su id crudo (Lissette 2026-09-11).
import { getPolicyStatusMeta } from '../policyStatuses.js';
// Columnas propias: la MISMA maquinaria de Renovaciones y Prospecto y Referido
// (Lissette 2026-09-11). Definiciones en los ajustes de la agencia, valores por
// fila en su propia tabla, y el servidor filtra por alcance.
import { normalizeColumns, opcionesDeColumna, tipoDeColumna } from '../customColumns.js';
import CeldaPropia from './CeldaPropia.jsx';
import CustomColumnsManager from './CustomColumnsManager.jsx';
import { guardarColumnasDeTabla } from '../columnasDeTabla.js';
import {
  getAgencySettings, updateAgencySettings, getCustomColumnValues, saveCustomColumnValues,
} from '../api.js';
import { updatePolicy as apiUpdatePolicy } from '../api.js';
// La cuenta por miembro vive suelta, sin React: una aritmetica de dinero metida
// dentro de un componente solo se puede comprobar mirandola.
import {
  personasAseguradas, razonesDisponibles,
} from '../comisionPorPersona.js';
// Los meses que dio dinero la poliza, con la regla de las canceladas.
import { mesesQueDioDinero } from '../mesesDePoliza.js';
import { anioDePoliza } from '../anioDePoliza.js';
// Salud por persona, Vida con adelanto, y lo demas por % de la prima
// (Lissette 2026-10-11). La aritmetica, fuera del componente.
import { formatoDeComision, MESES_DE_ADELANTO } from '../comisionPorTipo.js';
// La cuenta y el guardado, compartidos con el Reporte de Ventas: los dos lados
// tienen que decir lo mismo (Lissette 2026-10-11).
import { cuentaDeComision, valoresDeComision, polizaConComision } from '../comisionDePoliza.js';
import { primaElegida, sufijoDePeriodo } from '../primaDePoliza.js';

// El modulo de columnas propias de esta tabla. Sus filas son POLIZAS, igual que
// las de Renovaciones, asi que el servidor resuelve el alcance por el agente
// del cliente de cada una.
const MODULO_COLUMNAS = 'commissions';

export function CommissionsTableView({ clients, policies, lang, onPoliciesChange, onClientClick }) {
  const es = lang === 'es';
  const [typeF, setTypeF] = useState('all');
  const [carrierF, setCarrierF] = useState('all');
  const [yearF, setYearF] = useState('all');
  const [drafts, setDrafts] = useState({}); // policyId -> { pct, monthly, amt, perPerson, razon }
  const [savingId, setSavingId] = useState(null);
  // Columnas propias de la agencia para ESTA tabla, con la misma maquinaria que
  // Renovaciones y Prospecto y Referido: las definiciones en los ajustes, las
  // casillas por fila (y el servidor solo devuelve las que esta persona ve).
  const [colsPropias, setColsPropias] = useState([]);
  const [celdas, setCeldas] = useState({});
  const [showColMgr, setShowColMgr] = useState(false);
  const apiMode = typeof window !== 'undefined' && !!window.__deskteAuth;

  useEffect(() => {
    if (!apiMode) return;
    let vivo = true;
    getAgencySettings()
      .then((ajustes) => { if (vivo) setColsPropias(normalizeColumns(ajustes?.tableCustomColumns?.[MODULO_COLUMNAS])); })
      .catch(() => {});
    getCustomColumnValues(MODULO_COLUMNAS)
      .then((d) => { if (vivo) setCeldas(d || {}); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [apiMode]);

  // Se manda la fila ENTERA (el servidor reemplaza) y el estado local se
  // actualiza cuando el servidor dice que si: pintar primero y fallar despues
  // deja a la agente creyendo que quedo guardado.
  const guardarCelda = useCallback(async (rowId, colId, texto) => {
    const antes = celdas[rowId] || {};
    const nuevas = { ...antes };
    if (texto) nuevas[colId] = texto; else delete nuevas[colId];
    try {
      await saveCustomColumnValues(MODULO_COLUMNAS, rowId, nuevas);
      setCeldas((p2) => ({ ...p2, [rowId]: nuevas }));
    } catch (e) {
      toast.error((es ? 'No se pudo guardar: ' : 'Could not save: ') + (e.message || e));
    }
  }, [celdas, es]);

  // Solo se borra lo que se borró en la ventana (ticket de Marilyn Sequera,
  // 2026-10-28). Ver crm-app/columnasDeTabla.js.
  const guardarColumnas = useCallback(async (cols) => {
    setColsPropias(await guardarColumnasDeTabla(MODULO_COLUMNAS, colsPropias, cols));
  }, [colsPropias]);

  // Lo que ofrece cada columna de tipo LISTA: lo que la agencia ya escribio en
  // ella. Se deduce de las casillas y no se guarda aparte — una segunda copia
  // se desincroniza.
  const opcionesPorColumna = useMemo(() => {
    const m = {};
    for (const col of colsPropias) {
      if (tipoDeColumna(col) !== 'list') continue;
      m[col.id] = opcionesDeColumna(col, Object.values(celdas).map((f) => f && f[col.id]));
    }
    return m;
  }, [colsPropias, celdas]);

  const clientById = useMemo(() => Object.fromEntries((clients || []).map(c => [c.id, c])), [clients]);
  // El año sale de la MISMA pieza que el filtro de Pólizas y la esquina de la
  // tarjeta (Lissette 2026-09-13): con una copia por pantalla, dos sitios que
  // enseñan el año de la misma póliza pueden acabar diciendo años distintos.
  const yearOf = (p) => anioDePoliza(p);
  const carriers = useMemo(() => [...new Set((policies || []).map(p => p.carrier).filter(Boolean))].sort(), [policies]);
  const years = useMemo(() => [...new Set((policies || []).map(yearOf).filter(Boolean))].sort().reverse(), [policies]);

  const rows = useMemo(() => (policies || []).filter(p => {
    if (typeF !== 'all' && p.type !== typeF) return false;
    if (carrierF !== 'all' && (p.carrier || '') !== carrierF) return false;
    if (yearF !== 'all' && yearOf(p) !== yearF) return false;
    return true;
  }), [policies, typeF, carrierF, yearF]);

  const num = (v) => { const n = parseFloat(String(v ?? '').replace(/[^0-9.\-]/g, '')); return isNaN(n) ? 0 : n; };

  // CUANTOS MESES DIO DINERO. La cuenta vive fuera, en mesesDePoliza.js, porque
  // de ella sale dinero (total por mes = mensual x meses) y ahi si se puede
  // comprobar con pruebas. Incluye la regla de las canceladas: una poliza que se
  // cancelo dejo de dar dinero ese dia, no el dia que le tocaba renovar
  // (Lissette 2026-09-11: *"es para saber cuantos meses esa poliza dio dinero"*).
  const monthsInfo = (p) => mesesQueDioDinero(p);

  const draftFor = (p, k) => {
    const d = drafts[p.id];
    if (d && d[k] !== undefined) return d[k];
    if (k === 'pct') return p.commissionPercent || '';
    if (k === 'monthly') return p.commissionMonthly || '';
    if (k === 'perPerson') return p.comisionPorPersona || '';
    if (k === 'razon') return p.razonDeComision || '';
    if (k === 'pctPrima') return p.porcentajeDePrima || '';
    if (k === 'adelanto') return p.mesesDeAdelanto || '';
    return p.commissionAmount || '';
  };
  const setDraft = (id, k, v) => setDrafts(prev => ({ ...prev, [id]: { ...prev[id], [k]: v } }));

  // Cuantas personas cubre la poliza. Sale de a quien se marco en ELLA, que es
  // el mismo numero que enseña su formulario en "Miembros activos".
  const personasDe = (p) => personasAseguradas(p);

  // POR MIEMBRO manda primero: (por persona × personas) × %. Hacen falta las
  // dos cosas —personas y un importe por persona—, porque con una sola el total
  // seria $0 y la tabla estaria diciendo que esa poliza no deja nada.
  const porMiembro = (p) => ({
    personas: personasDe(p),
    porPersona: draftFor(p, 'perPerson'),
    porcentaje: draftFor(p, 'pct'),
  });

  // LA CUENTA DE CADA FILA vive en crm-app/comisionDePoliza.js, la MISMA que
  // usa el Reporte de Ventas: salud por persona, vida con adelanto, lo demas
  // por % de la prima una sola vez, y lo viejo como antes.
  const cuenta = (p) => cuentaDeComision(p, valoresDeComision(p, drafts[p.id] || {}));
  const computedTotal = (p) => cuenta(p).total;
  const totalCommission = rows.reduce((s, p) => s + computedTotal(p), 0);

  // Guarda con la MISMA funcion que el Reporte de Ventas: el total se deriva
  // de la cuenta, y si no cambio nada no se manda nada.
  const saveRow = async (p) => {
    const d = drafts[p.id];
    if (!d) return;
    setDrafts(prev => { const x = { ...prev }; delete x[p.id]; return x; });
    const { siguiente: next, cambio } = polizaConComision(p, d);
    if (!cambio) return;
    onPoliciesChange && onPoliciesChange(prev => (prev || []).map(x => x.id === p.id ? next : x));
    const apiMode = typeof window !== 'undefined' && !!window.__deskteAuth;
    if (apiMode) {
      setSavingId(p.id);
      try { await apiUpdatePolicy(p.id, next); }
      catch (e) { toast.error((es ? 'No se pudo guardar la comisión: ' : 'Could not save commission: ') + (e.message || e)); }
      finally { setSavingId(null); }
    }
  };

  // La operacion de cada total, para el titulo de su casilla.
  const explicarTotal = (p, cu, mi, porM, personas) => {
    const meses = `${mi.meses} ${es ? 'meses' : 'months'}${mi.asumido ? (es ? ' (asumido)' : ' (assumed)') : ''}`;
    const split = num(porM.porcentaje) > 0 ? ` × ${num(porM.porcentaje)}%` : ` × 100% (${es ? 'sin split' : 'no split'})`;
    if (cu.como === 'vida') {
      const v = cu.vida;
      return `${fmtMoney(num(p.monthlyPremium))} × 12 = ${fmtMoney(v.primaAnual)} × ${num(draftFor(p, 'pctPrima'))}%${split} = ${fmtMoney(v.total)}`;
    }
    if (cu.como === 'prima') { const pe = primaElegida(p); return `${fmtMoney(pe.monto)}${sufijoDePeriodo(pe.periodo, lang)} × ${num(draftFor(p, 'pctPrima'))}%${split} (${es ? 'una sola vez' : 'one time'})`; }
    if (cu.como === 'miembro') return `${fmtMoney(num(porM.porPersona))} × ${personas} ${es ? 'personas' : 'people'}${split}`;
    if (cu.como === 'mensual') return `${fmtMoney(num(draftFor(p, 'monthly')))} × ${meses} (${es ? 'comisión mensual guardada antes' : 'previously saved monthly commission'})`;
    return '';
  };

  const cellInp = { width: '100%', boxSizing: 'border-box', background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 6, color: 'var(--c-text,#c8dff0)', padding: '.25rem .4rem', fontSize: '.78rem', fontFamily: 'inherit', outline: 'none' };
  const flechaBtn = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 26, borderRadius: 8, background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-cyan,#00d4ff)' };
  const th = { padding: '.5rem .6rem', color: 'var(--c-text2,#7aa3c0)', fontWeight: 700, whiteSpace: 'nowrap', borderBottom: '1px solid var(--c-border,#1a3550)' };
  const td = { padding: '.4rem .6rem', whiteSpace: 'nowrap' };

  // Click-to-sort by any column (Lissette 2026-06-18). Default = client A→Z;
  // numbers/dates default to high→low (desc). Sorting %/monthly/total reflects
  // any unsaved inline edit (drafts) too.
  const [sortKey, setSortKey] = useState('client');
  const [sortDir, setSortDir] = useState('asc');
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir((['amt', 'pct', 'pctPrima', 'adelanto', 'prima', 'year', 'eff', 'renew', 'cancel', 'months', 'personas', 'perPerson'].includes(key)) ? 'desc' : 'asc'); }
  };
  const sortVal = (p, key) => {
    switch (key) {
      case 'pct': return num(draftFor(p, 'pct'));
      case 'pctPrima': return num(draftFor(p, 'pctPrima'));
      case 'adelanto': return num(draftFor(p, 'adelanto'));
      case 'amt': return computedTotal(p);
      case 'months': return monthsInfo(p).meses;
      case 'personas': return personasDe(p);
      case 'perPerson': return num(draftFor(p, 'perPerson'));
      case 'razon': return String(draftFor(p, 'razon') || '').toLowerCase();
      case 'eff': return String(p.effectiveDate || '');
      case 'renew': return String(p.renewalDate || '');
      case 'cancel': return String(p.cancellationDate || '');
      case 'year': return yearOf(p);
      case 'carrier': return String(p.carrier || '').toLowerCase();
      // Por el NOMBRE que se ve, no por el id: un estatus propio se guarda como
      // "cs_ab12" y ordenar por eso agruparia al azar.
      case 'status': return getPolicyStatusMeta(p.status, lang).label.toLowerCase();
      case 'prima': return primaElegida(p).monto;
      case 'type': { const pt = getPolicyType(p.type); return String((es ? pt.labelEs : pt.label) || '').toLowerCase(); }
      default: { const c = clientById[p.clientId]; return String(c ? formatClientName(c) : (p.clientName || '')).toLowerCase(); }
    }
  };
  // El desplegable de la razon se llena SOLO: lo de siempre mas todo lo que ya
  // se escribio en esa columna. Deducirlo de lo escrito es la unica copia que no
  // se puede desincronizar (Lissette: "que se vaya escribiendo y vaya quedando").
  const razones = useMemo(
    () => razonesDisponibles((policies || []).map(x => x.razonDeComision)),
    [policies],
  );
  const sortedRows = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = sortVal(a, sortKey); const vb = sortVal(b, sortKey);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [rows, sortKey, sortDir, drafts]);
  // ── DESPLAZAR LA TABLA A LO ANCHO ──────────────────────────────────────
  //
  // Lissette 2026-09-11: *"hacer el cuadro mas grande para poder ver todas las
  // columnas, y si aun queda pequeno coloca la opcion de flecha para poder [ir
  // a] la derecha, y tambien con las flechas del teclado"*.
  const scrollRef = useRef(null);
  const [desborde, setDesborde] = useState({ izq: false, der: false });
  // HASTA DONDE PUEDE CRECER LA TABLA. Se mide donde EMPIEZA la caja y se
  // estira hasta el borde de la ventana: centrarla con `margin-left: 50%` la
  // metia debajo del menu de la izquierda y cortaba la columna del cliente, que
  // es justo la que hay que leer para saber de quien es la fila.
  const cajaRef = useRef(null);
  const [anchoCaja, setAnchoCaja] = useState(null);
  const medirAncho = useCallback(() => {
    const el = cajaRef.current;
    if (!el || typeof window === 'undefined') return;
    const izq = el.getBoundingClientRect().left;
    // clientWidth y no innerWidth: innerWidth cuenta la barra de desplazamiento
    // de la pagina y la tabla se pasaria de ancho por esos pocos pixeles.
    const ventana = document.documentElement.clientWidth || window.innerWidth;
    setAnchoCaja(Math.max(320, Math.round(ventana - izq - 24)));
  }, []);
  useEffect(() => {
    medirAncho();
    window.addEventListener('resize', medirAncho);
    return () => window.removeEventListener('resize', medirAncho);
  }, [medirAncho]);
  const mirarDesborde = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // 2px de margen: el navegador redondea y si no, la flecha derecha se queda
    // encendida para siempre al final del todo.
    setDesborde({
      izq: el.scrollLeft > 2,
      der: el.scrollLeft + el.clientWidth < el.scrollWidth - 2,
    });
  }, []);
  useEffect(() => {
    mirarDesborde();
    const el = scrollRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', mirarDesborde, { passive: true });
    window.addEventListener('resize', mirarDesborde);
    return () => { el.removeEventListener('scroll', mirarDesborde); window.removeEventListener('resize', mirarDesborde); };
  }, [mirarDesborde, sortedRows.length, colsPropias.length]);
  const desplazar = (hacia) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: hacia * Math.max(240, el.clientWidth * 0.7), behavior: 'smooth' });
  };
  // CON EL TECLADO, pero sin robarle las flechas a quien esta escribiendo: esta
  // tabla esta llena de casillas, y mover la tabla mientras alguien corrige un
  // importe con las flechas seria peor que no tenerlo.
  const teclas = (e) => {
    if (estaEscribiendo(e.target)) return;
    if (e.key === 'ArrowRight') { e.preventDefault(); desplazar(1); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); desplazar(-1); }
  };

  const sortableTh = (key, label) => (
    <th onClick={() => toggleSort(key)} title={es ? 'Ordenar' : 'Sort'} style={{ ...th, cursor: 'pointer', userSelect: 'none', color: sortKey === key ? 'var(--c-cyan,#00d4ff)' : th.color }}>
      {label}{sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );

  return (
    <div>
      <div style={{ marginBottom: '1rem' }}>
        <div className="dkt-sec-title" style={{ margin: 0 }}>
          <BarChart2 size={16} style={{ marginRight: 6, verticalAlign: -2 }} />
          {es ? 'Comisiones por Póliza' : 'Commissions by Policy'}
        </div>
        <div className="dkt-sec-sub">
          {es ? 'Salud: $ por persona × personas × % split. Vida y Gastos Finales: prima anual × % de la prima × % split, con adelanto. Las demás: prima × % de la prima × % split, una sola vez.' : 'Health: $ per person × people × split %. Life and Final Expense: annual premium × % of premium × split %, with an advance. Everything else: premium × % of premium × split %, one time.'}
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem', marginBottom: '.75rem' }}>
        <Select label={es ? 'Tipo de póliza' : 'Policy type'} value={typeF} onChange={e => setTypeF(e.target.value)}>
          <option value="all">{es ? 'Todos los tipos' : 'All types'}</option>
          {POLICY_TYPES.map(pt => <option key={pt.id} value={pt.id}>{(pt.icon ? pt.icon + ' ' : '') + (es ? pt.labelEs : pt.label)}</option>)}
        </Select>
        <Select label={es ? 'Compañía' : 'Company'} value={carrierF} onChange={e => setCarrierF(e.target.value)}>
          <option value="all">{es ? 'Todas' : 'All'}</option>
          {carriers.map(c => <option key={c} value={c}>{c}</option>)}
        </Select>
        <Select label={es ? 'Año' : 'Year'} value={yearF} onChange={e => setYearF(e.target.value)}>
          <option value="all">{es ? 'Todos' : 'All'}</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </Select>
        {/* Sin servidor (la demo publica) no hay donde guardar una columna, asi
            que el boton no se ofrece en vez de ofrecerse y fallar al guardar. */}
        {apiMode && (
          <button type="button" className="dkt-btn ghost xs" style={{ alignSelf: 'flex-end' }} onClick={() => setShowColMgr(true)}
            title={es ? 'Crear tus propias columnas en esta tabla' : 'Create your own columns in this table'}>
            <ColumnsIcon size={12} /> {es ? 'Columnas' : 'Columns'}{colsPropias.length ? ` (${colsPropias.length})` : ''}
          </button>
        )}
      </div>

      {/* MAS ANCHO (Lissette 2026-09-11: *"hacer el cuadro mas grande para poder
          ver todas las columnas"*): esta tabla tiene quince columnas —mas las
          propias de la agencia— y vive dentro de una columna de contenido
          pensada para texto. Se estira hacia la DERECHA hasta el borde de la
          ventana. Si aun asi no cabe, estan las flechas y el teclado. */}
      <div ref={cajaRef} style={{ position: 'relative', width: anchoCaja ? `${anchoCaja}px` : '100%', maxWidth: 'none' }}>
        {(desborde.izq || desborde.der) && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.35rem', marginBottom: '.35rem' }}>
            <button type="button" onClick={() => desplazar(-1)} disabled={!desborde.izq}
              title={es ? 'Mover a la izquierda (flecha ←)' : 'Scroll left (← key)'}
              style={{ ...flechaBtn, opacity: desborde.izq ? 1 : .35, cursor: desborde.izq ? 'pointer' : 'default' }}>
              <ChevronLeft size={16} />
            </button>
            <button type="button" onClick={() => desplazar(1)} disabled={!desborde.der}
              title={es ? 'Mover a la derecha (flecha →)' : 'Scroll right (→ key)'}
              style={{ ...flechaBtn, opacity: desborde.der ? 1 : .35, cursor: desborde.der ? 'pointer' : 'default' }}>
              <ChevronRight size={16} />
            </button>
          </div>
        )}
        {/* tabIndex + onKeyDown: las flechas del teclado mueven la tabla cuando
            el foco esta en ella y NO se esta escribiendo en una casilla. */}
        <div ref={scrollRef} onKeyDown={teclas} tabIndex={0} className="dkt-card"
          style={{ overflowX: 'auto', padding: 0, outline: 'none' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.8rem' }}>
          <thead>
            <tr style={{ background: 'var(--c-surface2,#112538)', textAlign: 'left' }}>
              {sortableTh('client', es ? 'Cliente' : 'Client')}
              {sortableTh('type', es ? 'Tipo de póliza' : 'Policy type')}
              {sortableTh('carrier', es ? 'Compañía' : 'Company')}
              {sortableTh('status', es ? 'Estatus' : 'Status')}
              {sortableTh('prima', es ? 'Prima (cliente)' : 'Premium (client)')}
              {sortableTh('eff', es ? 'Fecha Vigencia' : 'Effective date')}
              {sortableTh('renew', es ? 'Fecha Renovación' : 'Renewal date')}
              {sortableTh('cancel', es ? 'Fecha Cancelación' : 'Cancellation date')}
              {sortableTh('months', es ? 'Meses' : 'Months')}
              {sortableTh('personas', es ? 'Personas' : 'People')}
              {sortableTh('perPerson', es ? '$ por persona' : '$ per person')}
              {sortableTh('pctPrima', es ? '% de la prima' : '% of premium')}
              {sortableTh('adelanto', es ? 'Meses de adelanto' : 'Advance months')}
              {sortableTh('pct', es ? '% Split' : 'Split %')}
              {sortableTh('razon', es ? 'Razón' : 'Reason')}
              {sortableTh('amt', es ? 'Comisión Total' : 'Total commission')}
              {colsPropias.map((col) => <th key={col.id} style={th}>{col.name}</th>)}
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 && <tr><td colSpan={16 + colsPropias.length} style={{ padding: '2rem', textAlign: 'center', color: 'var(--c-text3,#45688a)' }}>{es ? 'No hay pólizas para estos filtros' : 'No policies for these filters'}</td></tr>}
            {sortedRows.map(p => {
              const c = clientById[p.clientId];
              const cname = c ? formatClientName(c) : (p.clientName || '—');
              const pt = getPolicyType(p.type);
              const mi = monthsInfo(p);
              const formato = formatoDeComision(p.type);
              const cu = cuenta(p);
              const nada = <span style={{ color: 'var(--c-text3,#45688a)' }}>—</span>;
              const sm = getPolicyStatusMeta(p.status, lang);
              const personas = personasDe(p);
              const porM = porMiembro(p);
              // Se escribio el importe por persona pero no hay a quien contar.
              const faltanPersonas = num(porM.porPersona) > 0 && personas === 0;
              return (
                <tr key={p.id} style={{ borderBottom: '1px solid var(--c-border,#1a3550)' }}>
                  <td style={{ ...td, color: 'var(--c-white,#eef6ff)', fontWeight: 600 }}>
                    {onClientClick && c
                      ? <button type="button" onClick={() => onClientClick(c)} title={es ? 'Abrir ficha del cliente' : 'Open client'}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--c-cyan,#00d4ff)', fontWeight: 600, fontFamily: 'inherit', fontSize: 'inherit' }}>
                          {cname}<ExternalLink size={11} style={{ flexShrink: 0, opacity: .8 }} />
                        </button>
                      : cname}
                  </td>
                  <td style={td}>{(pt.icon ? pt.icon + ' ' : '') + (es ? pt.labelEs : pt.label)}</td>
                  <td style={td}>{p.carrier || '—'}</td>
                  <td style={td}>
                    <span style={{ fontSize: '.65rem', padding: '.2rem .55rem', borderRadius: 100, fontWeight: 700, whiteSpace: 'nowrap',
                      background: `${sm.color}1a`, color: sm.color, border: `1px solid ${sm.color}55` }}>
                      {sm.label}
                    </span>
                  </td>
                  {/* PRIMA: lo que paga el CLIENTE por esta poliza, traido tal
                      cual de la poliza (Lissette 2026-10-11). Solo se lee: se
                      cambia en la poliza, que es donde vive el dato. */}
                  {/* La prima que CUENTA (Lissette 2026-10-22): la elegida en la
                      póliza, o la que esté llena — mensual, semestral o anual. */}
                  {(() => { const pe = primaElegida(p); return (
                  <td style={{ ...td, fontVariantNumeric: 'tabular-nums', color: pe.monto > 0 ? 'var(--c-text,#c8dff0)' : 'var(--c-text3,#45688a)' }}
                    title={es ? 'Prima que paga el cliente, tomada de la póliza (la elegida, o la que esté llena). Se cambia en la póliza.' : 'Premium the client pays, taken from the policy (the chosen one, or whichever is filled). Change it on the policy.'}>
                    {pe.monto > 0 ? `${fmtMoney(pe.monto)}${sufijoDePeriodo(pe.periodo, lang)}`
                      : pe.llena ? `$0${sufijoDePeriodo(pe.periodo, lang)}` : '—'}
                  </td>); })()}
                  <td style={{ ...td, color: 'var(--c-text2,#7aa3c0)' }}>{p.effectiveDate ? fmt(p.effectiveDate) : '—'}</td>
                  <td style={{ ...td, color: 'var(--c-text2,#7aa3c0)' }}>{p.renewalDate ? fmt(p.renewalDate) : '—'}</td>
                  {/* FECHA DE CANCELACION: solo la tienen las canceladas, y es
                      la que decide hasta cuando dio dinero la poliza. */}
                  <td style={{ ...td, color: p.cancellationDate ? '#ef4444' : 'var(--c-text3,#45688a)' }}>
                    {p.cancellationDate ? fmt(p.cancellationDate) : '—'}
                  </td>
                  <td style={{ ...td, color: mi.porCancelacion ? '#ef4444' : (mi.asumido ? 'var(--c-amber,#ffb700)' : 'var(--c-text2,#7aa3c0)'), fontWeight: mi.porCancelacion ? 700 : 400 }}
                    title={mi.porCancelacion
                      ? (es ? `Meses que dio dinero: de la vigencia a la cancelación (${fmt(p.cancellationDate)}).` : `Months it earned: effective to cancellation (${fmt(p.cancellationDate)}).`)
                      : (mi.asumido ? (es ? 'Sin fecha de renovación; se asume 12 meses (anual). Edita la póliza para precisar.' : 'No renewal date; assuming 12 months (annual). Edit the policy to set it.') : '')}>
                    {mi.meses}{mi.asumido ? '*' : ''}{mi.porCancelacion ? '⊘' : ''}
                  </td>
                  {/* PERSONAS: automatica, de la poliza. No se puede escribir
                      aqui a proposito — el sitio donde se dice quien esta
                      asegurado es la poliza, y dos sitios para el mismo dato
                      acaban diciendo cosas distintas.

                      EL AMBAR SOLO CUANDO ESTORBA: hay muchas polizas sin nadie
                      marcado, asi que pintarlas todas de ambar seria un aviso en
                      casi cada fila — y un aviso que sale siempre deja de leerse.
                      Se enciende unicamente cuando alguien escribio un importe
                      por persona y no hay personas: ahi el 0 es lo que impide
                      que salga la cuenta, y hay que decirlo. */}
                  <td style={{ ...td, textAlign: 'center', fontVariantNumeric: 'tabular-nums', color: faltanPersonas ? 'var(--c-amber,#ffb700)' : 'var(--c-text2,#7aa3c0)', fontWeight: faltanPersonas ? 700 : 400 }}
                    title={formato !== 'salud' ? '' : faltanPersonas
                      ? (es ? 'Escribiste un importe por persona pero esta póliza no tiene a nadie marcado como asegurado: márcalos en la póliza y el total sale solo.' : 'You entered an amount per person but nobody is marked insured on this policy: mark them on the policy and the total computes itself.')
                      : (es ? 'Personas marcadas como aseguradas en la póliza' : 'People marked insured on the policy')}>
                    {formato === 'salud' ? personas : nada}
                  </td>
                  {/* $ POR PERSONA: solo salud (Lissette 2026-10-11: "el
                      formato de salud es solo para salud"). */}
                  <td style={{ ...td, minWidth: 104 }}>
                    {formato === 'salud'
                      ? <input value={draftFor(p, 'perPerson')} onChange={e => setDraft(p.id, 'perPerson', e.target.value)} onBlur={() => saveRow(p)} inputMode="decimal" placeholder="$/persona" style={cellInp} />
                      : nada}
                  </td>
                  {/* % DE LA PRIMA: lo que paga la aseguradora sobre la prima.
                      Vida y todo lo que no es salud. */}
                  <td style={{ ...td, minWidth: 92 }}>
                    {formato !== 'salud'
                      ? <input value={draftFor(p, 'pctPrima')} onChange={e => setDraft(p.id, 'pctPrima', e.target.value)} onBlur={() => saveRow(p)} inputMode="decimal" placeholder="%" style={cellInp}
                          title={es ? 'El porcentaje que paga la aseguradora sobre la prima.' : 'The percentage the carrier pays on the premium.'} />
                      : nada}
                  </td>
                  {/* MESES DE ADELANTO: solo Vida y Gastos Finales. En blanco = 9. */}
                  <td style={{ ...td, minWidth: 92 }}>
                    {formato === 'vida'
                      ? <input value={draftFor(p, 'adelanto')} onChange={e => setDraft(p.id, 'adelanto', e.target.value)} onBlur={() => saveRow(p)} inputMode="numeric" placeholder={String(MESES_DE_ADELANTO)} style={cellInp}
                          title={es ? `Cuántos meses adelanta la aseguradora. En blanco se entienden ${MESES_DE_ADELANTO}.` : `How many months the carrier advances. Blank means ${MESES_DE_ADELANTO}.`} />
                      : nada}
                  </td>
                  {/* % SPLIT: lo que nos queda. En TODAS las polizas (Lissette
                      2026-10-11: "todas las demas tambien, si es de agencia,
                      si es split"). */}
                  <td style={{ ...td, minWidth: 88 }}>
                    <input value={draftFor(p, 'pct')} onChange={e => setDraft(p.id, 'pct', e.target.value)} onBlur={() => saveRow(p)} inputMode="decimal" placeholder="%" style={cellInp}
                          title={es ? 'Lo que nos queda a nosotros (split). En blanco se entiende 100%.' : 'Our share (split). Blank means 100%.'} />
                  </td>
                  {/* RAZON: por que es ese split. Desplegable que se llena solo. */}
                  <td style={{ ...td, minWidth: 128 }}>
                    <Combobox value={draftFor(p, 'razon')} options={razones}
                          onChange={(v) => setDraft(p.id, 'razon', v)}
                          onPick={() => setTimeout(() => saveRow(p), 0)}
                          onBlur={() => saveRow(p)}
                          placeholder={es ? 'Split / Agencia' : 'Split / Agency'}
                          inputStyle={cellInp} lang={lang} maxLength={60} />
                  </td>
                  {cu.como === 'manual' ? (
                    <td style={{ ...td, minWidth: 110 }}>
                      <input value={draftFor(p, 'amt')} onChange={e => setDraft(p.id, 'amt', e.target.value)} onBlur={() => saveRow(p)} inputMode="decimal" placeholder="$" style={cellInp} />
                      {savingId === p.id ? ' …' : ''}
                    </td>
                  ) : (
                    // La cuenta va en el titulo: un numero de dinero sin la
                    // operacion detras obliga a rehacerla a mano para creerselo.
                    <td style={{ ...td, color: 'var(--c-green,#00ff88)', fontWeight: 700 }} title={explicarTotal(p, cu, mi, porM, personas)}>
                      {fmtMoney(cu.total)}{savingId === p.id ? ' …' : ''}
                      {cu.como === 'vida' && (
                        <div style={{ fontSize: '.66rem', fontWeight: 600, color: 'var(--c-text2,#7aa3c0)' }}>
                          {es
                            ? `Adelanto ${cu.vida.adelantoMeses} meses: ${fmtMoney(cu.vida.adelanto)}${cu.vida.mesesRestantes ? ` · luego ${fmtMoney(cu.vida.porMes)}/mes × ${cu.vida.mesesRestantes}` : ''}`
                            : `${cu.vida.adelantoMeses}-month advance: ${fmtMoney(cu.vida.adelanto)}${cu.vida.mesesRestantes ? ` · then ${fmtMoney(cu.vida.porMes)}/mo × ${cu.vida.mesesRestantes}` : ''}`}
                        </div>
                      )}
                    </td>
                  )}
                  {/* Las columnas propias de la agencia, con la misma celda que
                      Renovaciones y Prospecto y Referido: texto, desplegable que
                      se llena solo, o casilla. */}
                  {colsPropias.map((col) => (
                    <td key={col.id} style={{ ...td, minWidth: 120 }}>
                      <CeldaPropia
                        valor={(celdas[p.id] || {})[col.id] || ''}
                        lang={lang}
                        columna={col}
                        opciones={opcionesPorColumna[col.id] || []}
                        onGuardar={(txt) => guardarCelda(p.id, col.id, txt)} />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr style={{ background: 'var(--c-surface2,#112538)', fontWeight: 800 }}>
                <td colSpan={15} style={{ padding: '.5rem .6rem', textAlign: 'right', color: 'var(--c-text2,#7aa3c0)' }}>
                  {es ? 'Total comisión' : 'Total commission'} · {rows.length} {es ? 'pólizas' : 'policies'}
                </td>
                <td style={{ padding: '.5rem .6rem', color: 'var(--c-green,#00ff88)' }}>{fmtMoney(totalCommission)}</td>
                {/* Las columnas propias no suman dinero, pero la fila del total
                    tiene que llegar hasta el final: sin esto se corta a media
                    tabla y parece que falta algo. */}
                {colsPropias.length > 0 && <td colSpan={colsPropias.length} />}
              </tr>
            </tfoot>
          )}
        </table>
        </div>
      </div>
      <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', marginTop: '.5rem' }}>
        {es
          ? 'SALUD: escribe el $ por persona y el total sale solo = $ por persona × personas × % split. Las personas se traen de la póliza (quién está marcado como asegurado). VIDA Y GASTOS FINALES: escribe el % de la prima que paga la aseguradora; el total del primer año = prima × 12 × % de la prima × % split. Debajo sale el adelanto (en blanco se entienden 9 meses; cámbialo en «Meses de adelanto») y lo que queda por mes. LAS DEMÁS PÓLIZAS: escribe el % de la prima y el total = prima × % de la prima × % split, una sola vez (no se multiplica por los meses). La PRIMA se trae de la póliza. El % split y la Razón (Split / Agencia) van en todas; el split en blanco se entiende 100%. Si una póliza todavía no tiene su % escrito, su total se sigue contando como antes. MESES: de la vigencia a la renovación contando el último día (1/1 al 31/12 son 12); sin fecha de renovación se asumen 12 (*); si está cancelada, hasta la cancelación (⊘). SI NO CABE: muévela con las flechas de arriba o con ← → del teclado. COLUMNAS: con «Columnas» creas las tuyas.'
          : 'HEALTH: enter the $ per person and the total = $ per person × people × split %. People come from the policy (whoever is marked insured). LIFE AND FINAL EXPENSE: enter the % of premium the carrier pays; first-year total = premium × 12 × % of premium × split %. Below it you see the advance (blank means 9 months; change it under “Advance months”) and what is left per month. EVERYTHING ELSE: enter the % of premium and the total = premium × % of premium × split %, one time (not multiplied by months). The PREMIUM comes from the policy. Split % and Reason (Split / Agency) apply to every row; a blank split means 100%. Until a policy has its % entered, its total is counted the old way. MONTHS: effective to renewal counting the last day (1/1 to 12/31 is 12); no renewal date assumes 12 (*); cancelled counts to the cancellation (⊘). IF IT DOES NOT FIT: use the arrows above or ← → on the keyboard. COLUMNS: “Columns” creates your own.'}
      </div>

      {/* La ventana de crear/renombrar/ordenar/borrar columnas: la MISMA que
          Renovaciones, Prospecto y Referido, Baul, Info de Empresa y
          Seguimientos. Pinta su propio Modal, asi que no va envuelta en otro. */}
      {showColMgr && (
        <CustomColumnsManager columns={colsPropias} lang={lang} modulo={MODULO_COLUMNAS}
          title={es ? 'Columnas de Comisiones por Póliza' : 'Commissions by Policy columns'}
          onClose={() => setShowColMgr(false)} onSave={guardarColumnas} />
      )}
    </div>
  );
}
